"use no memo";

import type React from "react";

import {
  ClearIcon,
  PlusIcon,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  TextField,
} from "@stacklet/ui";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { RolePicker } from "../RolePicker/RolePicker";
import { ScopePicker } from "../ScopePicker";
import { type GroupFormSchema, getDefaultRole } from "./GroupSchema";

import type { GroupFormFields_query$key } from "./__generated__/GroupFormFields_query.graphql";

/**
 * Fragment that combines role picker and scope picker data requirements
 */
export const GroupFormFieldsFragment = graphql`
  fragment GroupFormFields_query on Query {
    ...RolePicker_roles
    ...ScopePicker_scopes
  }
`;

interface Props {
  queryRef: GroupFormFields_query$key;
  /**
   * Make the name and display-name fields read-only. Set for SSO-provisioned
   * groups, whose metadata is owned by the IdP and can't be edited here.
   */
  metadataReadOnly?: boolean;
  /** Action buttons rendered left-aligned below the last section */
  actionButtons?: React.ReactNode;
  /**
   * Membership editor rendered between the group's metadata and its roles.
   * Omitted when there is no membership to edit yet (a group being created) or
   * when an identity provider owns it.
   */
  membershipSection?: React.ReactNode;
}

export function GroupFormFields({
  queryRef,
  metadataReadOnly = false,
  actionButtons,
  membershipSection,
}: Props) {
  const data = useFragment(GroupFormFieldsFragment, queryRef);

  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<GroupFormSchema>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "roles",
  });

  // Watch all roles to determine which ones are already selected
  const allRoles = watch("roles");

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-lg">
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <TextField
              assistiveText={
                fieldState.error?.message ||
                "Group name from your identity provider"
              }
              isInvalid={!!fieldState.error}
              isReadOnly={metadataReadOnly}
              label="Group Name (Case Sensitive)"
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="displayName"
          render={({ field, fieldState }) => (
            <TextField
              assistiveText={
                fieldState.error?.message ||
                "A descriptive name if the group name isn't sufficient"
              }
              isInvalid={!!fieldState.error}
              isReadOnly={metadataReadOnly}
              label="Display Name"
              {...field}
            />
          )}
        />
      </div>

      {membershipSection ? (
        <div className="mt-xl">{membershipSection}</div>
      ) : null}

      <div className="mt-xl">
        <div className="flex items-center justify-between gap-md">
          <Heading
            className="text-label-medium-strong text-text-primary"
            level={3}
          >
            Roles and Target
          </Heading>
          <Button
            IconLeft={<PlusIcon className="h-4 w-4" />}
            className="shrink-0"
            onPress={() => append(getDefaultRole())}
            type="button"
            variant="tertiary"
          >
            Add another role
          </Button>
        </div>
        <div className="mt-lg flex flex-col gap-xl">
          {fields.map((field, index) => {
            const selectedRole = watch(`roles.${index}.role`);
            // Get all selected roles except the current one to prevent duplicates
            const otherSelectedRoles =
              allRoles
                ?.map((role, i) => (i !== index ? role.role : null))
                .filter((role): role is string => Boolean(role)) || [];

            return (
              <div className="flex flex-col gap-xl" key={field.id}>
                {index > 0 ? <Divider /> : null}
                <div>
                  <div className="flex gap-md">
                    <div className="min-w-0 flex-1">
                      <RolePicker
                        disabledOptions={otherSelectedRoles}
                        onChange={(value) => {
                          setValue(`roles.${index}.role`, value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue(
                            `roles.${index}.scope`,
                            value === "resource-viewer" ||
                              value === "policy-deployer"
                              ? "GRANT_ACCOUNTS_ALL"
                              : "GRANT_SYSTEM_ALL",
                            { shouldValidate: true, shouldDirty: true },
                          );
                        }}
                        rolesRef={data}
                        value={selectedRole}
                      />
                    </div>
                    {fields.length > 1 ? (
                      // Stretch to the picker's height, skip its label row (xl:
                      // label line height + gap) and centre on the dropdown, so
                      // the button stays centred whether or not a role is picked.
                      <div className="flex shrink-0 items-center self-stretch pt-xl">
                        <Button
                          aria-label={`Remove role ${index + 1}`}
                          onPress={() => remove(index)}
                          type="button"
                          variant="tertiary"
                        >
                          <ClearIcon
                            className="text-text-secondary"
                            size={20}
                          />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  {errors.roles?.[index]?.role ? (
                    <p className="mt-xs text-label-small text-text-negative">
                      {errors.roles[index].role?.message}
                    </p>
                  ) : null}
                </div>

                <div className="empty:hidden">
                  <Controller
                    control={control}
                    name={`roles.${index}.scope`}
                    render={({ field: { onChange, value } }) => (
                      <ScopePicker
                        index={index}
                        onChange={onChange}
                        queryRef={data}
                        selectedRole={selectedRole}
                        value={value}
                      />
                    )}
                  />
                  {errors.roles?.[index]?.scope ? (
                    <p className="mt-xs text-label-small text-text-negative">
                      {errors.roles[index].scope?.message}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}

          {errors.roles?.message ? (
            <p className="text-label-small text-text-negative">
              {errors.roles.message}
            </p>
          ) : null}
        </div>
      </div>

      {actionButtons ? (
        <ButtonGroup align="start" className="mt-xl">
          {actionButtons}
        </ButtonGroup>
      ) : null}
    </div>
  );
}
